import React, { useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import Grid from "@mui/material/Grid";
import { Alert, FormHelperText, TextField } from "@mui/material";

import styles from "../../styles/jss/nextjs-material-kit/pages/dashboardFormStyle.js";
import Button from "../CustomButtons/Button";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import ReCAPTCHA from "react-google-recaptcha";
import { client, prepareCaptchaInterceptor } from "../../request/client";
import SnackbarContent from "../Snackbar/SnackbarContent";
import Check from "@mui/icons-material/Check";
import { useContactRequest } from "../../request/api";

const useStyles = makeStyles()(styles);

export default function ContactForm({}) {
  const [contact, setContact] = React.useState({
    fullName: "",
    email: "",
    role: "",
    enquiryType: "",
    message: "",
  });

  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [contactError, setContactError] = useState(null);

  const { classes, cx } = useStyles();
  const recaptchaRef = useRef(null);
  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
  const { isMutating: isSubmitting, trigger: sendEmail } = useContactRequest();

  const handleInputChange = (event) => {
    setContact((contact) => ({
      ...contact,
      [event.target.name]: event.target.value,
    }));
  };

  /**
   * Order of steps for successful registration:
   * 1. Submit pressed: `prepareCaptchaAndRegister()`
   * 2. Captcha solved: `handleCaptchaChange()`
   * 3. Captcha new value callback sets token and calls `register()`
   */
  const prepareCaptchaAndRegister = (event) => {
    event.preventDefault();
    recaptchaRef.current.reset(); // Make it work even if a past solution failed or timed out.
    recaptchaRef.current.execute(); // Get a token invisibly, or via a challenge if necessary.
  };

  const resetCaptcha = () => {
    // registrationComplete being true should cause the callback to auto-login
    // instead of trying to register again.
    recaptchaRef.current.reset();
    recaptchaRef.current.execute();
  };

  /**
   * @param {?string} captchaCode
   */
  const handleCaptchaChange = (captchaCode) => {
    if (!captchaCode) {
      return;
    }

    // We pass this to `register()` directly because the timing didn't work when
    // using a setter and `useState(...)` while also naively calling a submission
    // method right after.
    submit(captchaCode);
  };

  /**
   * @param {?string} captchaCode
   */
  const submit = async (captchaCode) => {
    setErrors({});
    setContactError("");

    prepareCaptchaInterceptor(client, captchaCode);
    try {
      await sendEmail({
        body: contact,
      });

      window.gtag && window.gtag("event", "contact_form");

      setSuccess(true);
    } catch (e) {
      console.error(e);
      setContactError(
        e.response?.data?.errors?.length > 0
          ? e.response?.data?.errors[0]
          : e.response?.data?.message || e.message
      );
      setErrors(e.response?.data?.errors);
    }
  };

  if (success) {
    return (
      <>
        <SnackbarContent
          message="Thank you for your submission. We aim to respond within 2 working days."
          color="success"
          icon={Check}
        />
      </>
    );
  }

  return (
    <form className={cx(classes.form)} onSubmit={prepareCaptchaAndRegister}>
      <ReCAPTCHA
        sitekey={recaptchaSiteKey}
        onChange={handleCaptchaChange}
        ref={recaptchaRef}
        size="invisible"
      />

      <Grid container spacing={4}>
        {contactError != null && (
          <Grid item xs={12}>
            <Alert severity="error">{contactError}</Alert>
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <label htmlFor="fullName" className={classes.label}>
              Name*
            </label>
            <TextField
              error={!!errors?.fullName}
              helperText={errors?.fullName}
              required
              id="fullName"
              name="fullName"
              value={contact.fullName}
              onChange={handleInputChange}
              disabled={isSubmitting}
              variant="outlined"
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <label htmlFor="email" className={classes.label}>
              Email*
            </label>
            <TextField
              error={!!errors?.email}
              helperText={errors?.email}
              required
              id="email"
              name="email"
              type="email"
              value={contact.email}
              onChange={handleInputChange}
              disabled={isSubmitting}
              variant="outlined"
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <label htmlFor="role" className={classes.label}>
              I am a*
            </label>
            <Select
              fullWidth
              required
              id="role"
              name="role"
              value={contact.role}
              onChange={handleInputChange}
            >
              <MenuItem value="Careers Advisor">Careers Advisor</MenuItem>
              <MenuItem value="Employer">Employer</MenuItem>
              <MenuItem value="Industry Body">Industry Body</MenuItem>
              <MenuItem value="Media Agency">Media Agency</MenuItem>
              <MenuItem value="Parent">Parent</MenuItem>
              <MenuItem value="Press">Press</MenuItem>
              <MenuItem value="Student">Student</MenuItem>
              <MenuItem value="Teacher">Teacher</MenuItem>
              <MenuItem value="Training Provider">Training Provider</MenuItem>
              <MenuItem value="University">University</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
            <FormHelperText error={!!errors?.role}>
              {errors?.role}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <label htmlFor="title" className={classes.label}>
              Type of enquiry*
            </label>
            <Select
              fullWidth
              required
              id="enquiryType"
              name="enquiryType"
              value={contact.enquiryType}
              onChange={handleInputChange}
            >
              <MenuItem value="General Query">General Query</MenuItem>
              <MenuItem value="Tech Issue">Tech Issue</MenuItem>
              <MenuItem value="Advertising & Partnerships">
                Advertising & Partnerships
              </MenuItem>
              <MenuItem value="Editorial">Editorial</MenuItem>
            </Select>
            <FormHelperText error={!!errors?.enquiryType}>
              {errors?.enquiryType}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <label htmlFor="message" className={classes.label}>
              Message*
            </label>
            <TextField
              error={!!errors?.message}
              required
              id="message"
              name="message"
              multiline
              minRows={10}
              value={contact.message}
              onChange={handleInputChange}
              disabled={isSubmitting}
              variant="outlined"
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Button
            color="primary"
            fullWidth
            disabled={isSubmitting}
            type="submit"
          >
            {isSubmitting ? "Sending" : "Send"}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}
